import { Box, Button, Chip, Divider, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { FormattedMessage } from "react-intl";
import { useConstants } from "../hooks/UseConstants";
import AdminCourseFilePreview from "../components/course/AdminCourseFilePreview";

function SummaryCard({ icon, label, value, theme }) {
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    bgcolor: "#eff6ff",
                    color: "#0D358C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography fontWeight={800} sx={{ color: "#0f172a", overflowWrap: "anywhere" }}>{value}</Typography>
            </Box>
        </Box>
    );
}

function RequestDetails({ request, onClickClose, onClickReject, onClickAccept }) {
    const { language } = useConstants();
    const theme = useTheme();
    const isAr = language === "ar";
    const contents = request.contents?.filter((content) => content.content_type === 'CourseFile') || [];

    const formatDate = (value) => {
        if (!value) return isAr ? "غير متوفر" : "Unavailable";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "94%", md: "84%" },
                maxWidth: 1200,
                maxHeight: "94vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='request_details' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}><FormattedMessage id='request_details_description' /></Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto" }} className="none-view-scroll">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight={900} sx={{ color: "#0D358C" }}>
                            {language === 'en' ? request.name_en : request.name_ar}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isAr ? "راجع البيانات والمحتوى المرفوع قبل اتخاذ قرار القبول أو الرفض." : "Review the request details and uploaded content before approving or rejecting it."}
                        </Typography>
                    </Box>
                    <Chip
                        label={request.status ? String(request.status) : (isAr ? "قيد المراجعة" : "Under review")}
                        color={request.status === "accepted" ? "success" : request.status === "rejected" ? "error" : "warning"}
                        variant="outlined"
                    />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
                    <SummaryCard icon={<DescriptionOutlinedIcon />} label={isAr ? "رقم الطلب" : "Request ID"} value={request.id} theme={theme} />
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={<FormattedMessage id='course_name' />} value={language === 'en' ? request.name_en : request.name_ar} theme={theme} />
                    <SummaryCard icon={<PersonOutlineOutlinedIcon />} label={<FormattedMessage id='teacher_name' />} value={`${request.teacher?.first_name || ""} ${request.teacher?.last_name || ""}`.trim() || "-"} theme={theme} />
                    <SummaryCard icon={<CalendarMonthOutlinedIcon />} label={<FormattedMessage id='request_date' />} value={formatDate(request.created_at)} theme={theme} />
                    <SummaryCard icon={<FolderOutlinedIcon />} label={<FormattedMessage id='files_count' />} value={contents.length} theme={theme} />
                    <SummaryCard icon={<VisibilityOutlinedIcon />} label={isAr ? "حالة المراجعة" : "Review status"} value={request.status || (isAr ? "قيد المراجعة" : "Pending")} theme={theme} />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <FolderOutlinedIcon sx={{ color: "#0D358C" }} />
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }}>
                        <FormattedMessage id='uploaded_files' />
                    </Typography>
                </Box>

                <AdminCourseFilePreview
                    contents={request.contents || []}
                    language={language}
                    emptyHintAr="لا توجد ملفات مرفوعة ضمن هذا الطلب."
                    emptyHintEn="No uploaded files were found for this request."
                />

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button
                        onClick={() => { onClickReject(request.id, 'rejected'); onClickClose(); }}
                        variant="outlined"
                        color="error"
                        startIcon={<HighlightOffOutlinedIcon />}
                        sx={{ minWidth: 150 }}
                    >
                        <FormattedMessage id='reject' />
                    </Button>
                    <Button
                        onClick={() => { onClickAccept(request.id, 'accepted'); onClickClose(); }}
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineOutlinedIcon />}
                        sx={{ minWidth: 160, boxShadow: "0 4px 14px rgba(22,163,74,0.24)" }}
                    >
                        <FormattedMessage id='approve' />
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default RequestDetails;